'use client';

import { useEffect } from 'react';

/**
 * Optimized Meta Pixel Loader
 *
 * Performance optimizations:
 * 1. Captures fbclid immediately (0ms) - No attribution loss
 * 2. Stores attribution data in cookies before pixel loads
 * 3. Loads Meta Pixel script after delay (non-blocking)
 * 4. Integrates with existing event deduplication system
 */

interface MetaPixelLoaderProps {
  loadDelay?: number;
  pixelId: string;
}

export function MetaPixelLoader({ loadDelay = 2000, pixelId }: MetaPixelLoaderProps) {
  useEffect(() => {
    // Early return if pixelId is not provided
    if (!pixelId) return;

    // STEP 1: Capture fbclid immediately (0ms delay)
    captureFbclidImmediately();

    // STEP 2: Set up Meta Pixel stub (for early fbq() calls)
    setupPixelStub();

    // STEP 3: Load actual Meta Pixel script after delay (non-blocking)
    const timer = setTimeout(() => {
      loadMetaPixelScript(pixelId);
    }, loadDelay);

    return () => clearTimeout(timer);
  }, [pixelId, loadDelay]);

  return null;
}

/**
 * Capture fbclid from URL immediately and store in cookie
 */
function captureFbclidImmediately() {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');

    // Validate fbclid contains only expected characters (alphanumeric, hyphens, underscores)
    if (fbclid && /^[a-zA-Z0-9_-]+$/.test(fbclid)) {
      const timestamp = Date.now();
      const fbcValue = `fb.1.${timestamp}.${fbclid}`;

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 90);

      const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = `_fbc=${fbcValue}; expires=${expiryDate.toUTCString()}; path=/; domain=.${rootDomain}; SameSite=Lax`;
    }

    const existingFbp = getCookie('_fbp');
    if (!existingFbp) {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 2147483647);
      const fbpValue = `fb.1.${timestamp}.${random}`;

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 90);

      const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
      document.cookie = `_fbp=${fbpValue}; expires=${expiryDate.toUTCString()}; path=/; domain=.${rootDomain}; SameSite=Lax`;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Meta Pixel] Error capturing fbclid:', error);
  }
}

/**
 * Helper to get cookie value
 */
function getCookie(name: string): null | string {
  /* istanbul ignore next */
  if (typeof document === 'undefined') return null;

  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return matches ? decodeURIComponent(matches[1]) : null;
}

/**
 * Load the actual Meta Pixel script
 */
function loadMetaPixelScript(pixelId: string) {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return;

  try {
    const existingScript = document.querySelector('script[src*="fbevents.js"]');
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.async = true;

    script.onload = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).fbq) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).fbq('init', pixelId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).fbq('track', 'PageView');
      }
    };

    script.onerror = () => {
      // eslint-disable-next-line no-console
      console.error('[Meta Pixel] Failed to load script');
    };

    document.head.appendChild(script);

    // Inject noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Meta Pixel] Error loading script:', error);
  }
}

/**
 * Set up pixel stub for early fbq() calls
 */
function setupPixelStub() {
  /* istanbul ignore next */
  if (typeof window === 'undefined') return;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(window as any).fbq) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const n: any = ((window as any).fbq = function (...args: unknown[]) {
      n.callMethod ? n.callMethod(...args) : n.queue.push(args);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window as any)._fbq) (window as any)._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
  }
}

export default MetaPixelLoader;
